import { useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store';
import { selectPromiseSets, selectStandingDataLoaded, selectTimeBands } from '@/reducers/standingDataSlice';
import { FormDropdown } from '@portal_components/components/FormDropdown';
import { FormProvider, useForm } from 'react-hook-form';
import { FormDatePicker } from '@portal_components/components/FormDatePicker';
import { FormButton } from '@portal_components/components/FormButton';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { handleWindowBooking, setSelectedOffer } from '@/reducers/jbpGeneralSlice';
import { GenericLoadingElement } from '@/components/GenericLoadingElement';
import { List } from '@/components/List';
import { ListItem, feedbackColour } from '@/components/ListItem';
import { Modal } from '@/components/Modal';
import { Title } from '@portal_components/components/Title';
import { Typography } from '@/components/Typography';
import { useDefinedParams } from '@/hooks/useDefinedParams';
import { useJbpQuery, RestTypes } from '@/hooks/useJbpQuery';
import { useUserInfo } from '@/hooks/useUserInfo';
import { getServicePowerMappings } from '@/utils/getServicePowerMappings';
import { DateTime } from 'luxon';
import { Offer, OffersRequest, getOfferServicePowerErrorResponse, getOffersSuccessType } from '../../../../../APIDocs/apiTypes';
import { ApiError } from '../../ApiError';
import { Button } from '@/components/Button';

type BookJobOffersInternalFormValues = {
  preferredDate: string;
  isWindowJobBooking: boolean;
  timeSlot: string;
  timeBand: string;
  force: boolean;
};

//NON SUPPLIER VERSION OF THE OFFERS PROCESS, SPECIFICALLY TO ALLOW TIME BAND AND FORCE OPTIONS WHERE APPLICABLE.
export const BookJobOffersInternal = () => {
  const { isInternalUser, canAccess } = useUserInfo();
  const dispatch = useAppDispatch();
  const { site, jobDetails } = useAppSelector(state => state.jbp);
  const navigate = useNavigate();
  const showMoreIncrement = 7;
  const [showMoreCount, setShowMoreCount] = useState(showMoreIncrement);

  // Standing data from redux slice, always available as fetched on app load
  const promiseSets = useAppSelector(selectPromiseSets);
  const timeBands = useAppSelector(selectTimeBands);
  const standingDataLoaded = useAppSelector(selectStandingDataLoaded);

  useEffect(() => {
    setSelectedOffer(undefined);
  }, []);

  useEffect(() => {
    if (!isInternalUser) {
      navigate('/404');
    }
  }, [isInternalUser, navigate]);

  const [mpan, serviceLine] = useDefinedParams(['mpan', 'serviceLine']);

  //Modal controls
  const [showConfirmOfferSelection, setShowConfirmOfferSelection] = useState(false);
  const [showDateErrorModal, setShowDateErrorModal] = useState(false);

  const postCode = jobDetails?.siteAddress.postCode ?? site?.siteAddress.postCode;

  const defaultAllDayTimeBandStart = '0800';
  const defaultAllDayTimeBandEnd = '1700';

  type ExtendedOfferType = Offer & {
    earliestAppt?: boolean;
  };

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [filtersChanged, setFiltersChanged] = useState<boolean>(false);

  const initialQuery: OffersRequest = {
    mpxn: mpan,
    jobType: jobDetails?.jobTypeCode ?? '',
    fuel: 'E',
    siteMpid: site?.userMpid ?? '',
    postcode: postCode ?? '',
    startDate: site?.offerDateRange.startDate,
    endDate: site?.offerDateRange.endDate,
    timeBandStart: defaultAllDayTimeBandStart,
    timeBandEnd: defaultAllDayTimeBandEnd,
    force: false,
    promiseSetId: '',
    preferredDate: DateTime.now().toFormat('yyyy-MM-dd'),
    extraSkills: jobDetails?.extraSkills
  };

  const [queryData, setQueryDataForGetOffers] = useState<OffersRequest>(initialQuery);
  const formDefaultValues: BookJobOffersInternalFormValues = {
    force: false,
    preferredDate: `${DateTime.now().toISO({ precision: 'seconds', includeOffset: false })}Z`, // because stripping the time zone also takes off the Z
    isWindowJobBooking: false,
    timeSlot: 'All Day',
    timeBand: ''
  };

  const [lastSubmittedFormValues, setLastSubmittedFormValues] = useState<BookJobOffersInternalFormValues>({ ...formDefaultValues });

  const onGetOffersError = (err: getOfferServicePowerErrorResponse) => {
    console.log('Error returned from service power:' + err);
    setErrorMessage(err?.message ? `Error received from Service Power: ${err.message}` : getServicePowerMappings(err?.errorCode));
  };

  const {
    isLoading,
    data,
    refetch: refetchOffers,
    isFetching
  } = useJbpQuery<getOffersSuccessType, getOfferServicePowerErrorResponse>(RestTypes.Post, queryData, ['offersInternal'], `jbp/offers/request`, true, serviceLine, undefined, onGetOffersError);

  useEffect(() => {
    refetchOffers();
  }, [queryData, refetchOffers]);

  const onShowMoreClick = () => {
    setShowMoreCount(showMoreCount + showMoreIncrement);
  };

  const handleBookJobSelection = () => {
    navigate(`/bookingResult/${mpan}/${serviceLine}`);
  };

  const formatOffersToDisplay = (offersResponse: ExtendedOfferType[] | undefined): ExtendedOfferType[] => {
    if (offersResponse != undefined) {
      const newOffers: ExtendedOfferType[] = structuredClone(offersResponse);

      //identify the earliest offer date to highlight to the user.
      //extract the offer token of the earliest offer date.
      const sortedOffers = newOffers.sort((a, b) => {
        const offer = DateTime.fromISO(a.StartTime);
        const nextOffer = DateTime.fromISO(b.StartTime);
        return offer.toMillis() - nextOffer.toMillis();
      });

      const offerTokenOfEarliestDate = sortedOffers.length > 0 ? sortedOffers[0].OfferToken : null;

      //Add earliestAppt true/false flags to each offer based on the offer token match.
      offersResponse.forEach(offer => {
        if (offer.OfferToken === offerTokenOfEarliestDate) {
          offer.earliestAppt = true;
        } else {
          offer.earliestAppt = false;
        }
      });

      return offersResponse.filter((offer, index) => index < showMoreCount);
    }
    return [];
  };

  const offersToDisplay = formatOffersToDisplay(data);

  const isShowMoreButtonDisbled = () => {
    return data != undefined && data.length <= showMoreCount;
  };

  const showForceOption = () => {
    return canAccess('canForceOffers');
  };

  const validationSchema = Yup.object().shape({
    preferredDate: Yup.string()
      .required('Date is required')
      .test('is-valid-date', 'Invalid date', (date: string) => {
        const selectedDate = DateTime.fromISO(date);
        const currentDate = DateTime.now().startOf('day');
        const startDate = site?.offerDateRange.startDate;
        const offerDateRangeStart = startDate != null ? DateTime.fromFormat(startDate, 'yyyy-MM-dd').startOf('day') : currentDate;

        const endDate = site?.offerDateRange.endDate;
        const offerDateRangeEnd = endDate != undefined ? DateTime.fromFormat(endDate, 'yyyy-MM-dd').endOf('day') : null;

        const newSelectedDate = selectedDate?.startOf('day');

        if (newSelectedDate && date) {
          const isSelectedDateWithinOfferDateRange = newSelectedDate >= offerDateRangeStart && (offerDateRangeEnd === null || newSelectedDate <= offerDateRangeEnd);
          const isSelectedDateInFuture = newSelectedDate >= currentDate;
          if (!isSelectedDateInFuture) {
            setShowDateErrorModal(true);
            return false;
          }
          if (!isSelectedDateWithinOfferDateRange && !['SUBMETER', 'DNO', 'BNO'].includes(serviceLine)) {
            setShowDateErrorModal(true);
            return false;
          }
          return true;
        }
      }),
    timeSlot: Yup.string().required('Time Slot is required'),
    timeBand: Yup.string().when(['timeSlot'], ([timeSlot], schema) => {
      if (timeSlot === 'All Day') return schema;
      return schema.required('Time Band is required');
    })
  });

  const methods = useForm<BookJobOffersInternalFormValues>({
    defaultValues: formDefaultValues,
    resolver: yupResolver(validationSchema as any)
  });

  const formvalues = methods.watch();
  const { timeSlot, isWindowJobBooking, preferredDate, force } = formvalues;

  const timeSlotOptions = useMemo(() => {
    return (
      promiseSets?.reduce<{ title: string; value: string }[]>((acc, ps) => {
        const pSet = ps?.promiseSetDescription;
        if (pSet && !acc.some(x => x.value === pSet)) {
          acc.push({ title: pSet, value: pSet });
        }
        return acc;
      }, []) ?? []
    );
  }, [promiseSets]);

  const timeBandOptions = useMemo(() => {
    if (timeSlot === 'All Day' || !timeBands) return [];
    return timeBands?.reduce<{ title: string; value: string }[]>((acc, tb) => {
      if (tb.apptBandDescription && tb.promiseSetDescription === timeSlot && tb.appointmentBandId) {
        acc.push({ title: tb.apptBandDescription, value: tb.appointmentBandId });
      }
      return acc;
    }, []);
  }, [timeSlot, timeBands]);

  const formattedPreferredDate = preferredDate ? DateTime.fromISO(preferredDate).toFormat('yyyy-MM-dd') : '';

  useEffect(() => {
    //reset timeBand when timeSlot changes
    methods.setValue('timeBand', '');
  }, [timeSlot, methods]);

  const handleSubmit = (data: BookJobOffersInternalFormValues) => {
    setErrorMessage('');
    setFiltersChanged(false);
    console.log(data);
    if (data.isWindowJobBooking) {
      dispatch(handleWindowBooking({ windowBookingStartDate: data.preferredDate, force: data.force }));
      if (jobDetails?.isWarrantJob) {
        navigate(`/bookingResult/${mpan}/${serviceLine}`);
      } else {
        navigate(`/bookJob/communications/${mpan}/${serviceLine}`);
      }
      return;
    }
    const formattedDate = DateTime.fromISO(data.preferredDate).toFormat('yyyy-MM-dd');
    let sdTimeBand = timeBands?.find(tb => tb.appointmentBandId === data.timeBand);
    if (!sdTimeBand && data.timeBand) return;

    if (!sdTimeBand) {
      // This is used when All Day is selected as there is no time slot
      sdTimeBand = timeBands?.find(tb => tb.promiseSetDescription === data.timeSlot);
    }

    const [timeBandStart, timeBandEnd] =
      data.timeBand && sdTimeBand && sdTimeBand.apptBandDescription ? sdTimeBand.apptBandDescription.replaceAll(':', '').split(' - ') : [defaultAllDayTimeBandStart, defaultAllDayTimeBandEnd];

    setQueryDataForGetOffers(prevState => ({
      ...prevState,
      preferredDate: formattedDate,
      timeBandStart: timeBandStart,
      timeBandEnd: timeBandEnd,
      force: data.force,
      promiseSetId: sdTimeBand?.promiseSetId ?? '',
      timeBandId: sdTimeBand?.appointmentBandId
    }));
    setLastSubmittedFormValues({ ...data });
  };

  useEffect(() => {
    for (const key of Object.keys(lastSubmittedFormValues)) {
      if (formvalues[key as keyof BookJobOffersInternalFormValues] !== lastSubmittedFormValues[key as keyof BookJobOffersInternalFormValues]) {
        setFiltersChanged(true);
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formvalues]);

  const listItemClicked = (offer: Offer) => {
    dispatch(setSelectedOffer({ offer, force }));

    if (jobDetails?.isWarrantJob) {
      setShowConfirmOfferSelection(true);
    } else {
      navigate(`/bookJob/communications/${mpan}/${serviceLine}`);
    }
  };

  const title = 'Your appointment offers';

  console.log(`preffered date: ${preferredDate}`);

  return (
    <div className="mx-10 mb-12">
      <Title text={title} className="mt-7 mb-4"></Title>
      {standingDataLoaded && promiseSets !== undefined ? (
        <div>
          <div className="block">
            <Typography content={`Please select a date for the job. We have initially provided a list of offers. If you require a different date, please use the date picker.`} className="mb-3" />
          </div>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSubmit)}>
              <div className="grid grid-cols-2 gap-2">
                <FormDatePicker title="Select Date For Job" name="preferredDate" mandatory />
                <FormDropdown
                  title="Window Job"
                  mandatory
                  placeholder="Please Confirm whether this job is a window booking?"
                  name="isWindowJobBooking"
                  elementsForDisplay={[
                    { title: 'Yes', value: true },
                    { title: 'No', value: false }
                  ]}
                />
                {!isWindowJobBooking && (
                  <FormDropdown title="Time Slot" mandatory placeholder="Please Confirm whether this job is a window booking?" name="timeSlot" elementsForDisplay={timeSlotOptions} />
                )}
                {timeBandOptions && timeBandOptions?.length > 0 && !isWindowJobBooking && (
                  <FormDropdown title="Time Band" mandatory placeholder="Please Select the appointment timeband" name="timeBand" elementsForDisplay={timeBandOptions} />
                )}
                {showForceOption() === true && (
                  <FormDropdown
                    title="Force"
                    mandatory
                    placeholder="Force Job Onto Schedule?"
                    name="force"
                    elementsForDisplay={[
                      { title: 'Yes', value: true },
                      { title: 'No', value: false }
                    ]}
                  />
                )}
              </div>
              <FormButton primary={true} disabled={isLoading} type="submit" className="mb-2">
                {isWindowJobBooking ? 'Continue With Window Job Booking' : 'Search'}
              </FormButton>
            </form>
          </FormProvider>
        </div>
      ) : (
        <span></span>
      )}
      {isLoading || isFetching ? (
        <GenericLoadingElement count={showMoreIncrement} height="h-16" className="jbp-offers-loading w-4/6"></GenericLoadingElement>
      ) : errorMessage && !formvalues.isWindowJobBooking ? (
        <ApiError errorDescription={errorMessage} />
      ) : filtersChanged && !formvalues.isWindowJobBooking ? (
        <Typography content="Filters have changed since last search. Please submit the form to get updated offers." className="mt-2 text-feedback-red" />
      ) : offersToDisplay != undefined && offersToDisplay.length > 0 && !isWindowJobBooking ? (
        <div>
          <List title={title} data={offersToDisplay}>
            {/* USE THE TAG TEXT AND FEEDBACK COLOUR FOR EARLIEST APPT HIGHLIGHTING */}
            {offersToDisplay.map((offer, index) => {
              return (
                <ListItem
                  title="Appointment"
                  className="mr-10 w-4/6"
                  cells={[`${offer.ScheduledDate}`]}
                  feedbackColour={offer.earliestAppt === true ? feedbackColour.blue : feedbackColour.green}
                  key={index}
                  tagText={offer.earliestAppt === true ? 'Earliest' : 'Available'}
                  onClick={() => listItemClicked(offer)}
                />
              );
            })}
          </List>
          <Button text="Show more" primary={true} disabled={isShowMoreButtonDisbled()} onClick={onShowMoreClick} className="mt-2" />
        </div>
      ) : isWindowJobBooking ? (
        <div>
          <Typography content={`You have selected to book a window job. Please ensure a date has been selected before continuing.`} className="mb-3" />
        </div>
      ) : (
        <ApiError /> // fallback error where we don't have an error message for whatever reason
      )}
      {showDateErrorModal ? (
        <Modal title="Error">
          <Typography content={`The date you have selected is in the past and / or is not within your assigned appointment range.`} className="mb-3" />
          <Typography content={`Please select another date or contact Stark to arrange the appointment.`} className="mb-3" />
          <Button primary={true} text="OK" className="mr-4 mt-5" disabled={false} onClick={() => setShowDateErrorModal(false)} />
        </Modal>
      ) : (
        <span></span>
      )}
      {showConfirmOfferSelection ? (
        <Modal title="Continue with Job Booking?">
          <Typography content={`You have selected: ${formattedPreferredDate}.`} className="mb-3" />
          <Typography content={`If this is correct click Confirm to complete the booking.`} className="mb-3" />
          <Button primary={false} text="Cancel" className="mr-4 mt-5" disabled={false} onClick={() => setShowConfirmOfferSelection(false)} />
          <Button primary={true} text="Confirm" className="mr-4 mt-5" disabled={false} onClick={handleBookJobSelection} />
        </Modal>
      ) : (
        <span></span>
      )}
    </div>
  );
};
